import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { MessageSquare, Star, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useState } from 'react'

export interface EvaluationFeedbackData {
  vote: 'HELPFUL' | 'NOT_HELPFUL' | null
  rating: number | null
  comment: string
}

interface EvaluationFeedbackProps {
  className?: string
  title?: string
  description?: string
  onSubmit?: (data: EvaluationFeedbackData) => void
}

export function EvaluationFeedback({
  className,
  title = 'How useful was this review?',
  description = 'Your feedback helps us improve the analysis',
  onSubmit,
}: EvaluationFeedbackProps) {
  const [vote, setVote] = useState<'up' | 'down' | null>(null)
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = () => {
    onSubmit?.({
      vote: vote === 'up' ? 'HELPFUL' : vote === 'down' ? 'NOT_HELPFUL' : null,
      rating: rating > 0 ? rating : null,
      comment,
    })
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div
        className={cn(
          'rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] px-6 py-5 text-center',
          className
        )}
      >
        <p className="text-sm font-medium text-emerald-400">Thank you for your feedback!</p>
        <p className="mt-1 text-xs text-muted-foreground/80">
          Your input helps us improve review quality
        </p>
      </div>
    )
  }

  return (
    <div className={cn('rounded-xl border border-border bg-card/30 px-6 py-5', className)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground/80">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setVote(vote === 'up' ? null : 'up')}
            className={cn(
              'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors',
              vote === 'up'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-border text-muted-foreground hover:border-emerald-500/30 hover:bg-emerald-500/10 hover:text-emerald-400'
            )}
          >
            <ThumbsUp className="size-3.5" />
            Helpful
          </button>
          <button
            type="button"
            onClick={() => setVote(vote === 'down' ? null : 'down')}
            className={cn(
              'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors',
              vote === 'down'
                ? 'border-red-500/30 bg-red-500/10 text-red-400'
                : 'border-border text-muted-foreground hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400'
            )}
          >
            <ThumbsDown className="size-3.5" />
            Not helpful
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-[10px] text-muted-foreground/80">Rate accuracy</span>
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className={cn(
                'transition-colors',
                star <= (hoverRating || rating) ? 'text-amber-400' : 'text-muted-foreground/20'
              )}
            >
              <Star
                className="size-4"
                fill={star <= (hoverRating || rating) ? 'currentColor' : 'none'}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowComment(!showComment)}
          className={cn(
            'flex items-center gap-1 text-[10px] transition-colors lg:ml-auto',
            showComment
              ? 'text-foreground/60'
              : 'text-muted-foreground/80 hover:text-muted-foreground/80'
          )}
        >
          <MessageSquare className="size-3" />
          {showComment ? 'Hide comment' : 'Leave detailed feedback'}
        </button>
      </div>
      {showComment && (
        <div className="mt-3">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What was most or least useful? What would make this review more actionable?"
            className="w-full resize-none rounded-lg border border-border bg-background/50 px-3 py-2.5 text-xs text-foreground placeholder:text-muted-foreground/80 focus:border-[#f97316]/30 focus:outline-none"
            rows={3}
          />
        </div>
      )}
      {(vote || rating > 0 || showComment) && (
        <div className="mt-3 flex justify-end">
          <SecondaryButton size="sm" onClick={handleSubmit}>
            Submit feedback
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}
